import { Avatar, Box, Grid } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { addClient } from "../../redux/features/clientStateSlice";
import { createClient, formatPhoneNumbers, formatEmails } from "../../utils";

import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { ClientCreateStepper } from "./ClientCreateStepper";
import { ClientForm } from "./ClientForm";
import { ClientFormCoverages } from "./ClientFormCoverage";

import { getClientByUidApi } from "./actions/getClients.actions";

import { useDeleteContact } from "../virtual-receptionist/contacts/hooks/useContacts";
import { useModals } from "../../context/ModalContext";
import { ClientDetailsTabs } from "./ClientDetailsModal/ClientDetailsTabs";

export const ClientCreate = ({ contact = null, status = "ACTIVE", onCreated = () => {} }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [clientForm, setClientForm] = useState(null);

  const [coverageAreas, setCoverageAreas] = useState([]);
  const [coverageAreaSearch, setCoverageAreaSearch] = useState("");
  const [focusCoverageArea, setFocusCoverageArea] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const { showSnackbar } = useGlobalSnackbar();
  const dispatch = useDispatch();

  const token = sessionStorage.getItem("token");

  const { mutateAsync: deleteContact } = useDeleteContact(contact?.companyId || 0);

  const { closeByKey, openModal } = useModals();

  const handleNextStep = (data) => {
    setClientForm(data);
    setActiveStep(1);
  };

  const handleBackStep = () => {
    setActiveStep(0);
  };

  const handleFinish = async () => {
    if (!clientForm) {
      showSnackbar("Client information is missing.", "error");
      return;
    }

    setIsLoading(true);

    try {
      const { phone, phones, phoneNumbers, emails, email, ...clientForm_ } = clientForm || {};

      const mappedEmails = formatEmails(emails);
      const mappedPhoneNumbers = formatPhoneNumbers(
        phones?.length
          ? phones
          : phoneNumbers?.length
            ? phoneNumbers
            : phone
              ? [{ phone, dialCode: clientForm?.dialCode, countryCode: clientForm?.countryCode }]
              : [],
      );
      const mappedCoverageAreas = (coverageAreas || []).map((area) => ({
        ...area,
        address: area.address || "",
        city: area.city || "",
        state: area.state || "",
        country: area.country || "",
        zipCode: area.zipCode || "",
        latitude: Number(area.latitude ?? area.lat ?? 0),
        longitude: Number(area.longitude ?? area.lng ?? 0),
        milesOfCoverageArea: Number(area.milesOfCoverageArea ?? area.radius ?? 0),
      }));
      const primaryCoverageArea = mappedCoverageAreas[0] || {};
      const primaryEmail = mappedEmails[0]?.email || clientForm_?.email || null;
      const firstPhone = mappedPhoneNumbers[0] || null;
      const sanitizedMainPhone = (firstPhone?.phone || phone || "").toString().replace(/\D/g, "");
      const mainDialCode = firstPhone?.dialCode || clientForm_?.dialCode || clientForm?.dialCode || "+1";
      const mainCountryCode = firstPhone?.countryCode || clientForm_?.countryCode || clientForm?.countryCode || "US";

      const payload = {
        ...clientForm_,

        address: clientForm_.address || primaryCoverageArea.address || "",
        city: clientForm_.city || primaryCoverageArea.city || "",
        state: clientForm_.state || primaryCoverageArea.state || "",
        country: clientForm_.country || primaryCoverageArea.country || "",
        zipCode: clientForm_.zipCode || primaryCoverageArea.zipCode || "",

        email: primaryEmail,
        phone: sanitizedMainPhone,
        countryCode: mainCountryCode,
        dialCode: mainDialCode,

        clientCompanyName: clientForm_.type === "commercial" ? clientForm_.clientCompanyName || null : null,

        alias: clientForm_.alias || null,

        emails: mappedEmails,

        phoneNumbers: mappedPhoneNumbers,

        coverageAreas: mappedCoverageAreas,

        status: status || (contact?.type === "PROSPECT" ? "PROSPECT" : "ACTIVE"),
      };

      console.log("[ClientCreate] Payload:", payload);

      // =========================================================
      // CREAR CLIENTE
      // =========================================================

      const res = await createClient(token, payload);

      console.log("[ClientCreate] API response:", res);

      /*
       * createClient() retorna res.data de Axios.
       *
       * La respuesta del backend es:
       *
       * {
       *   status: 201,
       *   message: "Client created",
       *   data: {
       *     uid: "..."
       *   }
       * }
       *
       * Por lo tanto:
       *
       * res.data = cliente creado
       */

      const createdClient = res?.data;

      console.log("[ClientCreate] Created client:", createdClient);

      // =========================================================
      // VALIDAR UID
      // =========================================================

      if (!createdClient?.uid) {
        console.error("[ClientCreate] UID no encontrado:", res);

        throw new Error("Created client UID was not returned");
      }

      console.log("[ClientCreate] Created client UID:", createdClient.uid);

      // =========================================================
      // OBTENER CLIENTE COMPLETO
      // =========================================================

      const fullClient = await getClientByUidApi(createdClient.uid);

      console.log("[ClientCreate] Full client:", fullClient);

      // =========================================================
      // ACTUALIZAR REDUX
      // =========================================================

      dispatch(addClient(fullClient));

      // =========================================================
      // AVISAR AL PADRE
      // =========================================================

      await onCreated?.(fullClient);

      // =========================================================
      // CERRAR MODAL DE CREACIÓN
      // =========================================================

      closeByKey("Dashboard-ClientAdd-Business");

      // =========================================================
      // ABRIR DETALLE DEL CLIENTE
      // =========================================================

      openModal("Client-Details-Modal", <ClientDetailsTabs clientUid={fullClient?.uid || createdClient.uid} />);

      // =========================================================
      // ELIMINAR CONTACTO SI APLICA
      // =========================================================

      if (contact?.uid) {
        try {
          await deleteContact(contact.uid);
        } catch (deleteError) {
          console.error("[ClientCreate] Error deleting contact:", deleteError);
        }
      }

      // =========================================================
      // SUCCESS
      // =========================================================

      showSnackbar("Client created successfully.", "success");
    } catch (error) {
      console.error("[ClientCreate] Error creating client:", error);

      showSnackbar(error?.response?.data?.message || error?.message || "Failed to create client.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            mt: { xs: "3", md: "6" },
            height: "100%",
          }}
        >
          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mt: 6,
              }}
            />

            <ClientCreateStepper
              activeStep={activeStep}
              onBack={handleBackStep}
              hasCoverageAreas={coverageAreas.length > 0}
              isProspect={status === "PROSPECT"}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={10}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {activeStep === 0 && (
              <ClientForm contact={contact} clientForm={clientForm} onSubmitCreate={handleNextStep} isProspect={status === "PROSPECT"} />
            )}

            {activeStep === 1 && (
              <ClientFormCoverages
                mode="create"
                clientForm={clientForm}
                coverageAreas={coverageAreas}
                setCoverageAreas={setCoverageAreas}
                coverageAreaSearch={coverageAreaSearch}
                setCoverageAreaSearch={setCoverageAreaSearch}
                focusCoverageArea={focusCoverageArea}
                setFocusCoverageArea={setFocusCoverageArea}
                onSubmitCreate={handleFinish}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
